import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, HelpTip, Select } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { AVISO_FORA_DO_HORARIO, foraDoHorarioComercial } from '@/lib/horario-comercial';

/**
 * 🔴 A LIBERAÇÃO EM MASSA do coletor (mig 559) — pedida pelo Matheus na noite
 * em que a liberação para UM cliente derrubou o coletor dele (08/09/2026).
 * Por isso o desenho é desconfiado por padrão:
 *
 *   · a trava da VERSÃO PROVADA: só entra em lote a versão que já está
 *     reportando em pelo menos um cliente — forçar é gesto com confirmação;
 *   · ESCALONADO: um coletor por vez, e o próximo só entra quando o anterior
 *     voltou a reportar NA versão nova. Alguém não voltou? O lote PARA sozinho;
 *   · prévia NOMINAL antes de confirmar; PARAR e CANCELAR no meio.
 *
 * ⚠️ A liberação de um cliente só continua na ficha dele — este card não a
 * substitui, e é ação de administrador (o servidor cobra).
 */

interface ClienteComColetor {
  clienteId: string;
  razaoSocial: string;
  versao: string | null;
  ultimoContatoIso: string | null;
  versaoLiberada: string | null;
}

interface Pacote {
  versao: string;
  notas: string | null;
  publicado_em_iso: string;
  provada: boolean;
}

interface Lote {
  id: string;
  versao: string;
  estado: string;
  forcado: boolean;
  criadoPor: string | null;
  criadoEmIso: string;
  paradoMotivo: string | null;
  clientes: Array<{ clienteId: string; razaoSocial: string; ordem: number; estado: string; detalhe: string | null }>;
  atualizaram: number;
  naoVoltaram: number;
}

interface Dados {
  clientes: ClienteComColetor[];
  pacotes: Pacote[];
  lotes: Lote[];
}

const ESTADO_CLIENTE: Record<string, { rotulo: string; variant: 'neutral' | 'success' | 'warning' | 'danger' }> = {
  pendente: { rotulo: 'na fila', variant: 'neutral' },
  liberado: { rotulo: 'atualizando agora', variant: 'warning' },
  atualizou: { rotulo: 'na versão nova', variant: 'success' },
  nao_voltou: { rotulo: 'NÃO VOLTOU', variant: 'danger' },
  cancelado: { rotulo: 'cancelado', variant: 'neutral' },
};

export function LiberacaoColetorCard() {
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState('');
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);
  const [forcando, setForcando] = useState(false);

  const carregar = useCallback(() => {
    api
      .get<Dados>('/api/v1/monitoramento/atualizacao/lotes')
      .then((r) => {
        setDados(r);
      })
      .catch((e: Error & { status?: number }) => {
        // Quem não é admin simplesmente não vê o card — sem tela de erro.
        if (e.status !== 403) setErro(e.message);
        else setDados(null);
      });
  }, []);

  useEffect(() => {
    carregar();
    const id = window.setInterval(carregar, 30_000);
    return () => window.clearInterval(id);
  }, [carregar]);

  if (!dados) return null;

  const pacote = dados.pacotes.find((p) => p.versao === versao) ?? null;
  const emAndamento = dados.lotes.find((l) => l.estado === 'em_andamento') ?? null;
  const parado = dados.lotes.find((l) => l.estado === 'parado') ?? null;
  const alvos = dados.clientes.filter((c) => selecionados.has(c.clienteId) && c.versao !== versao);

  async function chamar(caminho: string, corpo?: unknown) {
    setErro(null);
    try {
      const r = await api.post<{ lotes: Lote[] }>(caminho, corpo ?? {});
      setDados((d) => (d ? { ...d, lotes: r.lotes } : d));
      carregar();
    } catch (err) {
      const e = err as Error & { response?: { campos?: Record<string, string[]>; mensagem?: string } };
      const campos = e.response?.campos;
      setErro(campos ? Object.values(campos)[0]?.[0] ?? e.message : (e.response?.mensagem ?? e.message));
    }
  }

  function alternar(id: string) {
    setSelecionados((s) => {
      const novo = new Set(s);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
    setConfirmando(false);
  }

  return (
    <Card className="space-y-3 p-4" data-testid="liberacao-coletor">
      <div>
        <h3 className="inline-flex items-center gap-1.5 font-medium text-ink-1 dark:text-ink-dark-1">
          Atualização do coletor — vários clientes
          <HelpTip texto="Escolha a versão e os clientes; a liberação é ESCALONADA: um coletor por vez, e o próximo só entra quando o anterior voltou a reportar na versão nova. Se alguém não voltar, o lote para sozinho. Só entra em lote a versão que já está reportando em pelo menos um cliente — a de 08/09 provou por quê. A liberação de um cliente só continua na ficha dele." />
        </h3>
        <p className="mt-0.5 text-sm text-ink-2 dark:text-ink-dark-2">
          Um coletor por vez, com freio automático. Quem já está na versão escolhida fica de fora
          sozinho.
        </p>
      </div>

      {erro && <Alert variant="danger" data-testid="liberacao-erro">{erro}</Alert>}

      {/* ─── O lote vivo (ou o que parou) — o número que diz se vai bem ─── */}
      {(emAndamento ?? parado) && (
        <div
          className="rounded-lg border border-line p-3 dark:border-line-dark"
          data-testid="lote-andamento"
        >
          {(() => {
            const l = (emAndamento ?? parado)!;
            return (
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink-1 dark:text-ink-dark-1">
                    Lote da {l.versao} · {l.estado === 'em_andamento' ? 'em andamento' : 'PARADO'}
                  </span>
                  <Badge variant={l.naoVoltaram > 0 ? 'danger' : 'success'} size="sm" data-testid="lote-progresso">
                    {l.atualizaram} de {l.clientes.length} na versão nova
                    {l.naoVoltaram > 0 ? ` · ${l.naoVoltaram} não voltou` : ''}
                  </Badge>
                </div>
                {l.paradoMotivo && (
                  <Alert variant="warning" data-testid="lote-parado-motivo">{l.paradoMotivo}</Alert>
                )}
                <ul className="space-y-0.5">
                  {l.clientes.map((c) => (
                    <li key={c.clienteId} className="flex flex-wrap items-center gap-2">
                      <Badge variant={ESTADO_CLIENTE[c.estado]?.variant ?? 'neutral'} size="sm">
                        {ESTADO_CLIENTE[c.estado]?.rotulo ?? c.estado}
                      </Badge>
                      <span className="text-ink-1 dark:text-ink-dark-1">{c.razaoSocial}</span>
                      {c.detalhe && (
                        <span className="text-xs text-ink-3 dark:text-ink-dark-3">{c.detalhe}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  {l.estado === 'em_andamento' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void chamar(`/api/v1/monitoramento/atualizacao/lotes/${l.id}/parar`)}
                      data-testid="lote-parar"
                    >
                      Parar o lote
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void chamar(`/api/v1/monitoramento/atualizacao/lotes/${l.id}/cancelar`)}
                    data-testid="lote-cancelar"
                    title="Para e desfaz a liberação de quem ainda não trocou de versão."
                  >
                    Cancelar o que falta
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ─── Montar um lote novo ─── */}
      {!emAndamento && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs text-ink-3 dark:text-ink-dark-3" htmlFor="lote-versao">
                Versão
              </label>
              <Select
                id="lote-versao"
                value={versao}
                onChange={(e) => {
                  setVersao(e.target.value);
                  setConfirmando(false);
                  setForcando(false);
                }}
                data-testid="lote-versao"
                className="min-w-[14rem]"
              >
                <option value="">Escolha a versão…</option>
                {dados.pacotes.map((p) => (
                  <option key={p.versao} value={p.versao}>
                    {p.versao}
                    {p.provada ? '' : ' — ainda não reportou de nenhum cliente'}
                  </option>
                ))}
              </Select>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setSelecionados((s) =>
                  s.size === dados.clientes.length
                    ? new Set()
                    : new Set(dados.clientes.map((c) => c.clienteId)),
                )
              }
              data-testid="lote-todos"
            >
              {selecionados.size === dados.clientes.length ? 'Desmarcar todos' : 'Marcar todos'}
            </Button>
          </div>

          {pacote && !pacote.provada && (
            <Alert variant="warning" data-testid="lote-nao-provada">
              🔴 Esta versão ainda não reportou de nenhum cliente. Libere para UM na ficha dele e
              confira que voltou — foi exatamente assim que a 1.2.0 derrubou o coletor do Exclusivo
              em 08/09. Dá para forçar, assumindo o risco.
            </Alert>
          )}

          <ul className="space-y-1" data-testid="lote-clientes">
            {dados.clientes.map((c) => (
              <li key={c.clienteId} className="flex flex-wrap items-center gap-2 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selecionados.has(c.clienteId)}
                    onChange={() => alternar(c.clienteId)}
                    data-testid={`lote-cliente-${c.clienteId}`}
                  />
                  <span className="text-ink-1 dark:text-ink-dark-1">{c.razaoSocial}</span>
                </label>
                <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {c.versao ? `versão ${c.versao}` : 'versão desconhecida'} · última notícia{' '}
                  {c.ultimoContatoIso ? formatDateTime(c.ultimoContatoIso) : 'nunca'}
                </span>
                {versao && c.versao === versao && (
                  <Badge variant="success" size="sm">já está nela</Badge>
                )}
              </li>
            ))}
          </ul>

          {/* 🔴 A PRÉVIA NOMINAL — a tela diz o que vai acontecer, antes. */}
          {confirmando && versao && alvos.length > 0 ? (
            <Alert variant="warning" data-testid="lote-previa">
              <p>
                Vai liberar a <strong>{versao}</strong> para <strong>{alvos.length}</strong>{' '}
                {alvos.length === 1 ? 'cliente' : 'clientes'}, UM POR VEZ — o próximo só entra
                quando o anterior voltar a reportar na versão nova. Cada coletor pergunta em até
                ~1 minuto depois da vez dele.
              </p>
              <p className="mt-1">{alvos.map((c) => c.razaoSocial).join(' · ')}</p>
              {/* ⚠️ Frente 5 (09/09): fora do horário comercial, AVISA — não impede. */}
              {foraDoHorarioComercial() && (
                <p className="mt-2 font-medium" data-testid="lote-fora-do-horario">
                  ⚠️ {AVISO_FORA_DO_HORARIO}
                </p>
              )}
              {pacote && !pacote.provada && (
                <label className="mt-2 flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={forcando}
                    onChange={(e) => setForcando(e.target.checked)}
                    data-testid="lote-forcar"
                  />
                  <span>
                    Entendo que esta versão nunca reportou de nenhum cliente e quero liberar mesmo
                    assim.
                  </span>
                </label>
              )}
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    void chamar('/api/v1/monitoramento/atualizacao/lotes', {
                      versao,
                      clienteIds: alvos.map((c) => c.clienteId),
                      forcar: forcando,
                    })
                  }
                  disabled={Boolean(pacote && !pacote.provada && !forcando)}
                  data-testid="lote-confirmar"
                >
                  Liberar, um por vez
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmando(false)}>
                  Voltar
                </Button>
              </div>
            </Alert>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={!versao || alvos.length === 0}
              onClick={() => setConfirmando(true)}
              data-testid="lote-revisar"
            >
              Revisar e liberar…
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
